import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { Session } from '../../api/session';
import { createJoinRequest, getObject, type ObjectDetails } from '../../api/tenant';
import { describePassError } from '../../domain/passErrors';
import type { TelegramApi } from '../../telegram/webapp';
import { MessageScreen } from '../auth/screens';
import f from '../pass-form/passForm.module.css';
import s from './more.module.css';

/** Join an object (request type 1). There is no object search in the API: the resident types the object number. */
const DONE_TEXT = 'УК проверит её и добавит вас к адресу. Статус — в разделе «Обращения».';
const NOT_FOUND = 'Объект не найден, проверьте код или название';
/**
 * The object card answers for any number in the database, even one residents cannot join (#4216: «ТЕСТControl»);
 * the join request then fails with v1's «Объект не найден, обратитесь к администратору системы».
 */
const OBJECT_NOT_FOUND = /object_not_found|объект не найден/i;

export function JoinScreen({ session, telegram, objectId, doneText = DONE_TEXT }: { session: Session; telegram: TelegramApi; objectId?: number; doneText?: string }) {
  const [params] = useSearchParams();
  const preset = objectId ?? (Number(params.get('object')) || undefined);
  const [number, setNumber] = useState(preset ? String(preset) : '');
  const [found, setFound] = useState<ObjectDetails | null>(null);
  const [address, setAddress] = useState('');
  const [error, setError] = useState<string | null>(null);

  const lookup = useMutation({
    mutationFn: () => getObject(session, Number(number)),
    onSuccess: (obj) => {
      setFound(obj ?? null);
      setError(obj ? null : NOT_FOUND);
    },
    onError: () => {
      setFound(null);
      setError(NOT_FOUND);
    },
  });
  const send = useMutation({
    mutationFn: () => createJoinRequest(session, { objectId: found!.id, address: address.trim() }),
    onSuccess: () => telegram.notify('success'),
    onError: (e) => {
      const general = describePassError(e).general;
      const code = (e as { code?: string }).code ?? '';
      if (OBJECT_NOT_FOUND.test(`${code} ${general ?? ''} ${(e as Error).message ?? ''}`)) {
        setFound(null);
        setError(NOT_FOUND);
      } else setError(general);
    },
  });

  // A посёлок link (startapp=obj_<id>) already knows the object: look it up right away.
  const autoLookup = useRef(false);
  useEffect(() => {
    if (!preset || autoLookup.current) return;
    autoLookup.current = true;
    lookup.mutate();
  }, [preset, lookup]);

  if (send.isSuccess) {
    return <MessageScreen title="Заявка отправлена" text={doneText} />;
  }

  return (
    <main className={f.sheet} aria-labelledby="join-title">
      <h1 id="join-title" className={f.title}>
        Присоединиться к объекту
      </h1>
      <p className={f.where}>Номер объекта сообщает управляющая компания посёлка.</p>
      <div className={f.field}>
        <label htmlFor="join-number" className={f.label}>
          Номер объекта
        </label>
        <input id="join-number" inputMode="numeric" className={f.input} value={number} onChange={(e) => { setNumber(e.target.value.replace(/\D/g, '')); setFound(null); }} />
      </div>
      <button type="button" className={s.small} disabled={!number || lookup.isPending} onClick={() => lookup.mutate()}>
        Найти
      </button>
      {found && <p className={s.preview}>{found.name}</p>}
      {found && (
        <div className={f.field}>
          <label htmlFor="join-address" className={f.label}>
            Ваш адрес в посёлке
          </label>
          <input id="join-address" className={f.input} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Улица, дом, квартира" />
        </div>
      )}
      <div className={f.footer}>
        {error && <p className={f.error} role="alert">{error}</p>}
        {found && (
          <button type="button" className={f.submit} disabled={!address.trim() || send.isPending} onClick={() => send.mutate()}>
            Отправить заявку
          </button>
        )}
      </div>
    </main>
  );
}
